import { useCopilotChatInternal as useCopilotChat } from '@copilotkit/react-core';
import { MessagesProps, useChatContext } from '@copilotkit/react-ui';
import { isEmpty } from 'lodash';
import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import BuglyCopilotIcon from '@/assets/images/header/bugly-copilot-icon.svg?react';
import { useAgentContext } from './agent';
import { agentService } from './agent-service';
import { MessageControlBar } from './control-bar';
import style from './index.module.scss';

/**
 * 将消息列表按"请求-响应"进行分组合并
 * 从一个 user 消息到下一个 user 消息之间的所有 assistant/tool 消息作为子消息
 * tool 消息的调用和结果合并成一条
 * 返回树形结构：每个消息可能包含 childMessages 数组
 */
function mergeMessages(messages: any[]): any[] {
  const merged: any[] = [];
  let currentGroup: any[] = [];

  for (const msg of messages) {
    if (msg.role === 'user') {
      // 遇到 user 消息，结束上一组
      if (currentGroup.length > 0) {
        // 处理当前组内的 tool 消息合并
        const processedGroup = mergeToolMessages(currentGroup);
        const firstMsg = processedGroup[0];

        // 将上一组的消息合并为一条，包含子消息
        const groupedMessage = {
          ...firstMsg, // 使用第一条消息作为父消息
          childMessages: processedGroup, // 将所有消息作为子消息
          id: firstMsg.id,
        };
        merged.push(groupedMessage);
        currentGroup = [];
      }
      // 添加 user 消息（user 消息不需要合并）
      merged.push(msg);
    } else {
      currentGroup.push(msg);
    }
  }

  // 处理最后一组
  if (currentGroup.length > 0) {
    const processedGroup = mergeToolMessages(currentGroup);
    const firstMsg = processedGroup[0];
    const groupedMessage = {
      ...firstMsg,
      childMessages: processedGroup,
      id: firstMsg.id,
    };
    merged.push(groupedMessage);
  }
  return merged;
}

/**
 * 合并 tool 消息：将 toolCall 和对应的 tool 结果合并成一条消息
 * 同一 toolCallId 的多条流式 ResultMessage 按 delta 累加拼接，
 * 含 toolCalls 的 assistant 消息若有文本内容也会保留输出
 *
 * 注意三点，均为并行/多 subagent 场景下的已知坑：
 * 1. toolName 必须优先取 toolCall 本身携带的 function.name——AG-UI 协议里
 *    ToolCallResultEvent 不带工具名，tool 消息的 toolName 常年是 undefined /
 *    上游兜底值 "unknown"，不能直接拿它当结果消息的展示名。
 * 2. 尚未收到结果的 toolCall 必须在遍历到它时就地占位插入 result，结果到达后
 *    原地替换成 merged 消息；不能等循环结束再统一 push 到末尾——一旦有多个
 *    并行 toolCall 且完成顺序不一致，末尾补齐会把"仍在执行中"的那条顶到最后，
 *    造成显示顺序和实际调用顺序错位。
 * 3. 部分 agent 后端（如多 subagent 协调器）的 ResultMessage.toolCallId 与发起调用时
 *    的 id 不一致（观测到的现象：调用卡片一直转圈不结束，同时凭空多出一张名字为
 *    "unknown" 的已完成卡片）。这种情况下按 id 精确匹配必然失败，兜底改为 FIFO
 *    匹配最早一个尚未收到结果的 toolCall——协议本身保证同一时刻的调用结果按发起顺序
 *    到达，代价是理论上不完全并行乱序完成的场景会配错，但两害相权取其轻：不兜底则
 *    每次都 100% 出现"unknown + 卡死转圈"，兜底后只有极端乱序完成时才可能配错。
 */
function mergeToolMessages(messages: any[]): any[] {
  const result: any[] = [];
  const toolCallMap = new Map<string, any>();
  const pendingOrder: string[] = [];
  const mergedToolMap = new Map<string, any>();

  for (const msg of messages) {
    if (msg.role === 'assistant' && msg.toolCalls) {
      // assistant 消息自身带有文本内容时，保留文本部分单独输出
      if (msg.content) {
        result.push({ ...msg, toolCalls: undefined });
      }
      // 逐个 toolCall 就地占位（保持原始顺序），结果到达后按 id 原地替换
      msg.toolCalls.forEach((call: any) => {
        const placeholder = { ...msg, toolCalls: [call] };
        result.push(placeholder);
        toolCallMap.set(call.id, { call, resultIndex: result.length - 1 });
        pendingOrder.push(call.id);
      });
    } else if (msg.role === 'tool') {
      const { toolCallId } = msg;
      const existingMerged = toolCallId ? mergedToolMap.get(toolCallId) : null;

      if (existingMerged) {
        // 流式 delta：累加拼接而非覆盖
        existingMerged.isStreaming = true;
        if (msg.content) {
          existingMerged.toolResult += msg.content;
        }
      } else {
        let matchedId = toolCallId;
        let toolCallInfo = toolCallId ? toolCallMap.get(toolCallId) : null;
        if (!toolCallInfo && pendingOrder.length > 0) {
          [matchedId] = pendingOrder;
          toolCallInfo = toolCallMap.get(matchedId);
        }

        const mergedToolMsg = {
          ...msg,
          id: `tool-merged-${toolCallId || msg.id}`,
          role: 'tool',
          toolName: toolCallInfo?.call?.function?.name || msg.toolName,
          toolCallId,
          toolCall: toolCallInfo?.call,
          toolResult: msg.content || '',
          isMergedTool: true,
        };

        if (toolCallInfo) {
          // 原地替换占位，保持它在调用发起时的位置
          result[toolCallInfo.resultIndex] = mergedToolMsg;
          toolCallMap.delete(matchedId);
          const idx = pendingOrder.indexOf(matchedId);
          if (idx >= 0) pendingOrder.splice(idx, 1);
        } else {
          result.push(mergedToolMsg);
        }

        if (toolCallId) {
          mergedToolMap.set(toolCallId, mergedToolMsg);
        }
      }
    } else {
      result.push(msg);
    }
  }

  return result;
}

export const CustomCopilotMessages = ({
  inProgress,
  children,
  RenderMessage,
  AssistantMessage,
  UserMessage,
  ErrorMessage,
  ImageRenderer,
  onRegenerate,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  markdownTagRenderers,
  chatError,
}: MessagesProps) => {
  const { labels } = useChatContext();
  const { messages: visibleMessages, interrupt } = useCopilotChat();

  const initialMessages = useMemo(() => makeInitialMessages(labels.initial), [labels.initial]);
  const messages = [...initialMessages, ...visibleMessages];
  const { messagesContainerRef, messagesEndRef } = useScrollToBottom(messages);
  const {
    currentAgent,
    agentOptions,
    setQuickText,
    suggestions: contextSuggestions,
    emptyDescription,
  } = useAgentContext();

  // 默认建议问题列表
  const defaultSuggestions = [
    '近7天发布了哪些版本？',
    '今天的应用崩溃率是多少？',
    '最近一周有没有突然劣化的异常？',
    '应用最新版本是否有劣化和新增的问题？',
    '生成本周应用的质量报告。',
  ];

  const exampleSuggestions = contextSuggestions ?? defaultSuggestions;

  // Determine which render component to use
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MessageRenderer = RenderMessage;

  const quickStart = (message: string) => {
    setQuickText(message);
  };

  const agentMessages = useMemo(() => {
    const newMessages = messages.map((msg) => {
      if (agentService.has(msg.id)) {
        const agent = agentService.get(msg.id);
        return {
          ...msg,
          agentId: agent.agentId,
          agentName: agent.agentName,
        };
      }

      const msgAgent = agentOptions.find(opt => opt.value === `${currentAgent}`);

      agentService.set(msg.id, {
        agentId: msgAgent?.value ?? '',
        agentName: msgAgent?.label ?? '',
      });

      return {
        ...msg,
        agentId: msgAgent?.value ?? '',
        agentName: msgAgent?.label ?? '',
      };
    });

    // setAgentMap(newMap);
    return newMessages;
  }, [messages]);

  // 基于处理后的消息进行合并
  const mergedMessages = useMemo(() => mergeMessages(agentMessages), [agentMessages]);

  useEffect(() => {
    window.__MESSAGE_AGENT_MAP = new Map();
  }, []);

  if (isEmpty(messages)) {
    return (
      <div className={style.emptyStateOverlay}>
        <div className={style.emptyStateContent}>
          <BuglyCopilotIcon className={style.emptyStateIcon} />
          <h2 className={style.emptyStateTitle}>
          👋 欢迎使用Bugly Copilot
          </h2>
          <p className={style.emptyStateDescription}>
            {emptyDescription || (
              <>
                我可以帮助您分析崩溃数据、查询趋势变化、对比版本差异等。<br />
                请在下方输入您的问题开始对话。
              </>
            )}
          </p>
          <div className={style.emptyStateExamples}>
            {exampleSuggestions.map(example => (
              <div
                key={example}
                className={style.emptyStateExampleCard}
                onClick={() => quickStart(example)}
              >
                {example}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="copilotKitMessages" ref={messagesContainerRef}>
      <div className="copilotKitMessagesContainer">
        {isEmpty(messages) && (
          <div className={style.emptyStateOverlay}>
            <div className={style.emptyStateContent}>
              <BuglyCopilotIcon className={style.emptyStateIcon} />
              <h2 className={style.emptyStateTitle}>
              👋 欢迎使用Bugly Copilot
              </h2>
              <p className={style.emptyStateDescription}>
                {emptyDescription || (
                  <>
                    我可以帮助您分析崩溃数据、查询趋势变化、对比版本差异等。<br />
                    请在下方输入您的问题开始对话。
                  </>
                )}
              </p>
              <div className={style.emptyStateExamples}>
                {exampleSuggestions.map(example => (
                  <div
                    key={example}
                    className={style.emptyStateExampleCard}
                    onClick={() => quickStart(example)}
                  >
                    {example}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {mergedMessages.map((mergedMsg, index) => {
          const isLastMessage = index === mergedMessages.length - 1;
          // 判断是否是最后一条 assistant 消息
          const isLastAssistantMessage = mergedMsg.role === 'assistant' && (() => {
            for (let i = index + 1; i < mergedMessages.length; i++) {
              if (mergedMessages[i].role === 'assistant') {
                return false;
              }
            }
            return true;
          })();
          const isCurrentMessage = inProgress && isLastMessage;

          if (
            mergedMsg?.childMessages?.length === 1
            && !mergedMsg.childMessages?.[0]?.content
            && !isCurrentMessage
          ) {
            return null;
          }

          if (mergedMsg.childMessages) {
            return (
              <div key={mergedMsg.id} className="copilotKitMessage copilotKitMessageGroup">
                {mergedMsg?.agentId && <div
                  className="copilotKitMessageGroupHeader"
                >
                  {(() => {
                    const currentAgentOption = agentOptions.find(opt => opt.value === mergedMsg.agentId);
                    return (
                      <span className='copilotKitMessageGroupHeaderIcon'>
                        {currentAgentOption?.icon || agentOptions?.[0]?.icon}
                      </span>
                    );
                  })()}
                  <span>{mergedMsg?.agentName}</span>
                </div>}
                <div className="copilotKitMessageGroupContent">
                  {mergedMsg.childMessages.map((childMsg: any, childIndex: number) => {
                    const isLastChild = childIndex === mergedMsg.childMessages.length - 1;
                    const isCurrentChild = isCurrentMessage && isLastChild;

                    return (
                      <MessageRenderer
                        key={childIndex}
                        message={childMsg}
                        inProgress={isCurrentChild ? inProgress : false}
                        index={childIndex}
                        isCurrentMessage={isCurrentChild}
                        AssistantMessage={AssistantMessage}
                        UserMessage={UserMessage}
                        ImageRenderer={ImageRenderer}
                        onRegenerate={onRegenerate}
                        onCopy={onCopy}
                        onThumbsUp={onThumbsUp}
                        onThumbsDown={onThumbsDown}
                        markdownTagRenderers={markdownTagRenderers}
                      />
                    );
                  })}
                </div>
                {mergedMsg.role === 'assistant' && <MessageControlBar
                  isCurrentMessage={isCurrentMessage}
                  isLastAssistantMessage={isLastAssistantMessage}
                  inProgress={inProgress}
                  onRegenerate={onRegenerate}
                  onCopy={onCopy}
                  onThumbsUp={onThumbsUp}
                  onThumbsDown={onThumbsDown}
                  message={mergedMsg}
                />}
              </div>
            );
          }

          return (
            <Fragment>
              <MessageRenderer
                key={index}
                message={mergedMsg}
                inProgress={inProgress}
                index={index}
                isCurrentMessage={isCurrentMessage}
                AssistantMessage={AssistantMessage}
                UserMessage={UserMessage}
                ImageRenderer={ImageRenderer}
                onRegenerate={onRegenerate}
                onCopy={onCopy}
                onThumbsUp={onThumbsUp}
                onThumbsDown={onThumbsDown}
                markdownTagRenderers={markdownTagRenderers}
              />
              {mergedMsg.role === 'assistant' && <MessageControlBar
                isCurrentMessage={isCurrentMessage}
                isLastAssistantMessage={isLastAssistantMessage}
                inProgress={inProgress}
                onRegenerate={onRegenerate}
                onCopy={onCopy}
                onThumbsUp={onThumbsUp}
                onThumbsDown={onThumbsDown}
                message={mergedMsg}
              />}
            </Fragment>
          );
        })}
        {interrupt}
        {chatError && ErrorMessage && <ErrorMessage error={chatError} isCurrentMessage />}
      </div>
      <footer className="copilotKitMessagesFooter" ref={messagesEndRef}>
        {children}
      </footer>
    </div>
  );
};

function makeInitialMessages(initial: string | string[] | undefined): any[] {
  if (!initial) return [];

  if (Array.isArray(initial)) {
    return initial.map(message => ({
      id: message,
      role: 'assistant',
      content: message,
    }));
  }

  return [
    {
      id: initial,
      role: 'assistant',
      content: initial,
    },
  ];
}

export function useScrollToBottom(messages: any[]) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const isUserScrollUpRef = useRef(false);

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      isProgrammaticScrollRef.current = true;
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }

    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      isUserScrollUpRef.current = scrollTop + clientHeight < scrollHeight;
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    const mutationObserver = new MutationObserver(() => {
      if (!isUserScrollUpRef.current) {
        scrollToBottom();
      }
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    isUserScrollUpRef.current = false;
    scrollToBottom();
  }, [messages.filter(m => m.role === 'user').length]);

  return { messagesEndRef, messagesContainerRef };
}
